/**
 * formatDate(date) → "2024.01.01"
 */
export function formatDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

/**
 * "2024.01.01" → "2024-01-01"  (for <input type="date">)
 */
export function toInputDate(d) {
  return d ? d.replaceAll(".", "-") : "";
}

/**
 * "2024-01-01" → "2024.01.01"
 */
export function toDisplayDate(d) {
  return d ? d.replaceAll("-", ".") : "";
}

/**
 * Get per-user registered date, fallback to global registeredDate
 */
export function getBookRegisteredDate(book, userId) {
  if (userId && book.registeredBy && book.registeredBy[userId]) {
    return book.registeredBy[userId];
  }
  return book.registeredDate || "";
}

/**
 * getUserDisplayName(user) → "이름" || "이메일앞" || "독자"
 */
export function getUserDisplayName(user) {
  if (!user) return "독자";
  return user.displayName || user.email?.split("@")[0] || "독자";
}

/**
 * normalizeBookId(id) → Number or String
 */
export function normalizeBookId(id) {
  return isNaN(id) ? id : Number(id);
}

/**
 * randomPlanetColors — 행성 색상 랜덤 생성
 */
/**
 * getPlanetStyle(user) → CSS variable object for planet colors
 */
export function getPlanetStyle(user) {
  if (!user?.planetColors) return {};
  return { "--orb1": user.planetColors[0], "--orb2": user.planetColors[1], "--orb3": user.planetColors[2] };
}

export function randomPlanetColors() {
  const hue = Math.floor(Math.random() * 360);
  const h = (n) => `hsl(${(hue + n) % 360},${40 + Math.random() * 20}%,`;
  return [
    h(0) + `${55 + Math.random() * 15}%)`,
    h(10) + `${30 + Math.random() * 10}%)`,
    h(20) + `${12 + Math.random() * 8}%)`,
  ];
}

/**
 * 색조(0~359) 하나에서 행성 3색을 만든다.
 * randomPlanetColors 와 같은 공식이되 채도·명도를 중간값으로 고정해,
 * 고를 때마다 같은 색이 나오도록 한다.
 */
export function colorsFromHue(hue) {
  const h = (n) => (hue + n) % 360;
  return [
    `hsl(${h(0)},50%,62%)`,
    `hsl(${h(10)},48%,35%)`,
    `hsl(${h(20)},44%,16%)`,
  ];
}

/** "hsl(347,58%,61%)" -> 347. 형식이 아니면 null */
export function hueOf(color) {
  const m = /^hsl\(\s*([\d.]+)/.exec(String(color || ""));
  return m ? Math.round(Number(m[1])) : null;
}

/** 오늘 가입했는가. 한국 기준 — UTC 로 자르면 새벽 가입자가 어제가 된다. */
const seoulDay = (d) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);

export function isNewToday(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt);
  if (Number.isNaN(t.getTime())) return false;
  return seoulDay(t) === seoulDay(new Date());
}

/**
 * 주격 조사. 받침이 있으면 '이', 없으면 '가'.
 *
 * 책 제목이라 숫자로 끝나는 것이 흔하다(『모방범 1』). 숫자는 읽는 소리로
 * 본다 — 0·1·3·6·7·8 은 영·일·삼·육·칠·팔로 받침이 있고 2·4·5·9 는 없다.
 * 한글도 숫자도 아니면 판단하지 않고 '이(가)' 로 둔다.
 */
export function subjectParticle(word) {
  const t = String(word ?? "").trim();
  if (!t) return "이(가)";
  const last = t[t.length - 1];
  const code = last.charCodeAt(0);
  if (code >= 0xac00 && code <= 0xd7a3) return (code - 0xac00) % 28 ? "이" : "가";
  if (last >= "0" && last <= "9") return "01368".includes(last) ? "이" : "가";
  return "이(가)";
}
